import { Link, useLocation, useParams } from "react-router-dom";
import { AnnotationHistoryList } from "../components/AnnotationHistoryList/AnnotationHistoryList";
import { AnnotationReviewPanel } from "../components/AnnotationReviewPanel/AnnotationReviewPanel";
import { AnnotationStatusBadge } from "../components/AnnotationStatusBadge/AnnotationStatusBadge";
import { SanitisationForm } from "../components/SanitisationForm/SanitisationForm";
import { GoldenRowPreview } from "../components/GoldenRowPreview/GoldenRowPreview";
import { TraceCitations } from "../components/TraceCitations/TraceCitations";
import { TraceConversation } from "../components/TraceConversation/TraceConversation";
import { TraceObservabilityLinks } from "../components/TraceObservabilityLinks/TraceObservabilityLinks";
import { FeedbackValueBadge } from "../components/FeedbackValueBadge/FeedbackValueBadge";
import { useAnnotationWorkflow, useFeedbackThread, useFeedbackTrace } from "../hooks";
import { buildAnswersByMessageIndex } from "../utils/buildAnswersByMessageIndex";
import { extractAnswerCitations } from "../utils/extractAnswerCitations";
import styles from "./FeedbackDetailScreen.module.css";

function parseAssistantMessageIndex(value: string | undefined): number | null {
  if (!value) {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function FeedbackDetailTraceView({
  trace,
  workspaceId,
  threadId,
}: {
  trace: NonNullable<ReturnType<typeof useFeedbackTrace>["data"]>;
  workspaceId: string;
  threadId: string;
}) {
  const threadQuery = useFeedbackThread({ workspaceId, threadId });
  const workflow = useAnnotationWorkflow(trace);
  const chunkCitations = threadQuery.data
    ? extractAnswerCitations(
        threadQuery.data.messages,
        trace.selected_assistant_message_index,
      )
    : undefined;
  const answersByMessageIndex = threadQuery.data
    ? buildAnswersByMessageIndex(threadQuery.data.messages)
    : undefined;

  const selectedMessage =
    trace.messages[trace.selected_assistant_message_index];
  const activeAnnotation =
    workflow.annotation ??
    workflow.historyItems.find(
      (item) => item.annotation_id === workflow.selectedAnnotationId,
    );
  const hasTraceAnnotationSummary = Boolean(trace.annotation?.annotation_id);
  const isAnnotationOpen = Boolean(workflow.selectedAnnotationId);

  return (
    <>
      <header className={styles.header}>
        <h1 className={styles.title}>
          {trace.thread_title ?? "Feedback trace"}
        </h1>
        <div className={styles.metaRow}>
          <span>Jurisdiction: {trace.jurisdiction ?? "—"}</span>
          <span className={styles.metaItem}>
            Feedback: <FeedbackValueBadge value={trace.feedback_value} />
          </span>
          <span>Thread: {trace.thread_id}</span>
        </div>
      </header>

      <div className={styles.layout}>
        <div className={styles.mainColumn}>
          <TraceConversation
            messages={trace.messages}
            selectedAssistantMessageIndex={trace.selected_assistant_message_index}
            answersByMessageIndex={answersByMessageIndex}
          />
          <TraceCitations
            citations={selectedMessage?.citations}
            chunkCitations={chunkCitations}
            isLoadingChunkCitations={threadQuery.isLoading}
            knowledgeOrgs={trace.knowledge_orgs}
          />
          <TraceObservabilityLinks links={trace.observability} />
        </div>

        <aside className={styles.sideColumn}>
          <div className={styles.sidePanel} data-testid="annotation-summary-panel">
            {hasTraceAnnotationSummary && !isAnnotationOpen ? (
              <>
                <h2 className={styles.sidePanelTitle}>Annotation</h2>
                <AnnotationStatusBadge status={trace.annotation?.status} />
                <dl className={styles.annotationMeta}>
                  <div>
                    Sanitised: {trace.annotation?.sanitised ? "Yes" : "No"}
                  </div>
                  <div>
                    Exported: {trace.annotation?.exported ? "Yes" : "No"}
                  </div>
                  {trace.annotation?.reviewer_assigned && (
                    <div>Reviewer: {trace.annotation.reviewer_assigned}</div>
                  )}
                </dl>
                <button
                  type="button"
                  className={styles.openAnnotationButton}
                  onClick={() =>
                    workflow.setSelectedAnnotationId(
                      trace.annotation!.annotation_id,
                    )
                  }
                >
                  Open annotation
                </button>
              </>
            ) : (
              <>
                <AnnotationHistoryList
                  items={workflow.historyItems}
                  selectedAnnotationId={workflow.selectedAnnotationId}
                  onSelect={workflow.setSelectedAnnotationId}
                />

                <AnnotationReviewPanel
                  key={`review-${activeAnnotation?.annotation_id ?? "new"}`}
                  annotation={activeAnnotation}
                  isLoading={
                    workflow.isLoadingAnnotation || workflow.isLoadingHistory
                  }
                  isSubmitting={workflow.isSubmitting}
                  isCreating={workflow.isCreating}
                  errorMessage={workflow.reviewErrorMessage}
                  reviewerIdentity={workflow.reviewerIdentity}
                  onCreateAndStartReview={() => {
                    void workflow.createAndStartReview();
                  }}
                  onStartReview={() => {
                    void workflow.startReview();
                  }}
                  onApprove={(input) => {
                    void workflow.approve(input);
                  }}
                  onReject={(input) => {
                    void workflow.reject(input);
                  }}
                />

                <SanitisationForm
                  status={activeAnnotation?.status}
                  sanitised={activeAnnotation?.sanitised}
                  reviewerIdentity={workflow.reviewerIdentity}
                  isSubmitting={workflow.isSubmitting}
                  errorMessage={workflow.sanitiseErrorMessage}
                  onSubmit={(input) => {
                    void workflow.markSanitised(input);
                  }}
                />

                <GoldenRowPreview
                  key={`preview-${activeAnnotation?.annotation_id ?? "new"}`}
                  trace={trace}
                  annotation={activeAnnotation}
                  reviewerIdentity={workflow.reviewerIdentity}
                  isSubmitting={workflow.isSubmitting}
                  errorMessage={workflow.exportErrorMessage}
                  onMarkExported={(input) => {
                    void workflow.markExported(input);
                  }}
                />
              </>
            )}
          </div>
        </aside>
      </div>
    </>
  );
}

function FeedbackDetailContent({
  workspaceId,
  threadId,
  assistantMessageIndex,
}: {
  workspaceId: string;
  threadId: string;
  assistantMessageIndex: number;
}) {
  const query = useFeedbackTrace({
    workspaceId,
    threadId,
    assistantMessageIndex,
  });

  if (query.isLoading) {
    return <p className={styles.message}>Loading feedback trace…</p>;
  }

  if (query.isError || !query.data) {
    return (
      <div className={styles.errorBanner} data-testid="trace-error">
        Unable to load feedback trace.
        <button
          type="button"
          className={styles.retryButton}
          onClick={() => query.refetch()}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <FeedbackDetailTraceView
      trace={query.data}
      workspaceId={workspaceId}
      threadId={threadId}
    />
  );
}

export function FeedbackDetailScreen() {
  const { workspaceId, threadId, assistantMessageIndex: indexParam } =
    useParams();
  const location = useLocation();
  const assistantMessageIndex = parseAssistantMessageIndex(indexParam);
  const listPath = `/eval-steward${location.search}`;

  if (!workspaceId || !threadId || assistantMessageIndex == null) {
    return (
      <div className={styles.screen}>
        <p className={styles.message}>
          Missing or invalid feedback item parameters.
        </p>
      </div>
    );
  }

  const breadcrumbLabel =
    threadId.length > 12 ? `${threadId.slice(0, 12)}…` : threadId;

  return (
    <div className={styles.screen}>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to={listPath} className={styles.breadcrumbLink}>
          Eval Steward
        </Link>
        <span className={styles.breadcrumbSep}>/</span>
        <span className={styles.breadcrumbCurrent}>{breadcrumbLabel}</span>
      </nav>

      <FeedbackDetailContent
        workspaceId={workspaceId}
        threadId={threadId}
        assistantMessageIndex={assistantMessageIndex}
      />
    </div>
  );
}
